function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Failed to read image'));
    reader.readAsDataURL(blob);
  });
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not decode image'));
    img.src = src;
  });
}

/**
 * CSP-safe blob: → data URL (img-src allows blob:; connect-src often blocks fetch(blob:)).
 * @param {string} blobUrl
 * @returns {Promise<string>}
 */
async function blobUrlToDataUrl(blobUrl) {
  try {
    const img = await loadImageElement(blobUrl);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    if (!canvas.width || !canvas.height) throw new Error('Invalid image dimensions');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');
    ctx.drawImage(img, 0, 0);
    // Prefer JPEG for API upload size; keep alpha-free salon photos.
    return canvas.toDataURL('image/jpeg', 0.92);
  } catch (err) {
    // Last resort: XHR can read same-origin blob: without connect-src in some browsers.
    const blob = await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', blobUrl, true);
      xhr.responseType = 'blob';
      xhr.onload = () => {
        if (xhr.status === 0 || (xhr.status >= 200 && xhr.status < 300)) {
          resolve(xhr.response);
        } else {
          reject(new Error('Could not read preview image'));
        }
      };
      xhr.onerror = () => reject(err || new Error('Could not read preview image'));
      xhr.send();
    });
    return blobToDataUrl(blob);
  }
}

/**
 * Normalize blob:, https:, or data: image refs for API upload (e.g. QR download-qr).
 * @param {string} imageRef
 * @returns {Promise<string>} data URL
 */
export async function imageRefToDataUrl(imageRef) {
  const raw = String(imageRef || '').trim();
  if (!raw) throw new Error('No image');
  if (raw.startsWith('data:')) return raw;
  if (raw.startsWith('blob:')) {
    return blobUrlToDataUrl(raw);
  }
  if (/^https?:\/\//i.test(raw)) {
    const res = await fetch(raw, { credentials: 'include' });
    if (!res.ok) throw new Error('Could not load preview image');
    return blobToDataUrl(await res.blob());
  }
  throw new Error('Unsupported image format');
}

/**
 * Re-encode any image ref to JPEG data URL (native AVIF/WebP breaks OpenCV face-lock).
 * @param {string} imageRef
 * @param {number} [quality]
 * @returns {Promise<string>}
 */
export async function ensureJpegDataUrl(imageRef, quality = 0.92) {
  const dataUrl = await imageRefToDataUrl(imageRef);
  if (/^data:image\/jpe?g(;|,)/i.test(dataUrl)) return dataUrl;
  const img = await loadImageElement(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;
  if (!canvas.width || !canvas.height) throw new Error('Invalid image dimensions');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable');
  ctx.drawImage(img, 0, 0);
  return canvas.toDataURL('image/jpeg', quality);
}
